import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBullseye, faLightbulb, faHandshake, faUsers,
    faArrowRightLong, faBuilding, faChartBar, faStar,
} from '@fortawesome/free-solid-svg-icons';
import AOS from 'aos';
import 'aos/dist/aos.css';

import work1 from '../../assets/images/optimized/workSample1.jpg';
import work2 from '../../assets/images/optimized/workSample2.jpg';
import work3 from '../../assets/images/optimized/workSample3.jpg';
import '../../styles/About.css';

const STATS = [
    { value: '5+',   label: 'Years of Experience',    icon: faStar },
    { value: '500+', label: 'Projects Completed',      icon: faBuilding },
    { value: '200+', label: 'Happy Clients',           icon: faUsers },
    { value: '10+',  label: 'States Served',           icon: faChartBar },
];

const VALUES = [
    {
        icon: faBullseye,
        title: 'Precision',
        desc: 'Every estimate we deliver is meticulously reviewed for accuracy, ensuring no cost is overlooked and every detail is communicated clearly.',
    },
    {
        icon: faLightbulb,
        title: 'Innovation',
        desc: 'We integrate advanced estimating software, AI-driven tools, and modern plugins to deliver faster and more accurate results.',
    },
    {
        icon: faHandshake,
        title: 'Transparency',
        desc: 'We believe in open communication at every stage. Clients always know exactly where their project stands and why.',
    },
];

function About() {
    useEffect(() => {
        AOS.init({ duration: 900, once: true, easing: 'ease-out-cubic' });
    }, []);

    return (
        <div className="about-page">

            {/* ── Hero Banner ── */}
            <section className="about-hero">
                <div className="about-hero-overlay" />
                <div className="about-hero-content" data-aos="fade-up">
                    <h1>About Us</h1>
                    <p className="about-breadcrumb">
                        <Link to="/">Home</Link>
                        <span>/</span>
                        About Us
                    </p>
                </div>
            </section>

            {/* ── Who We Are ── */}
            <section className="about-section">
                <div className="about-container about-two-col">
                    <div className="about-text" data-aos="fade-right">
                        <span className="about-label">Our Story</span>
                        <h2>Who We Are</h2>
                        <p>
                            At Surya Group, our name embodies our core values in the construction
                            estimating industry. "Surya" reflects clarity, precision, and
                            transparency — hallmarks of our estimation process, ensuring every
                            detail is accurately accessed and communicated.
                        </p>
                        <p>
                            "Group" symbolises the breadth of our services, encompassing all
                            facets of construction budgeting and estimating. Together, these
                            terms define our commitment to delivering comprehensive, systematic,
                            and explicit cost solutions — empowering clients with data-driven
                            insights for informed project decisions.
                        </p>
                        <Link to="/contact-us" className="about-cta-link">
                            Work With Us <FontAwesomeIcon icon={faArrowRightLong} />
                        </Link>
                    </div>
                    <div className="about-image-wrap" data-aos="fade-left">
                        <img width="966" height="679" loading="lazy" decoding="async" src={work1} alt="Surya Group construction work" className="about-img" />
                        <div className="about-img-badge">
                            <strong>Trusted</strong>
                            <span>across North America</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Stats ── */}
            <section className="about-stats-section">
                <div className="about-container">
                    <div className="about-stats-grid">
                        {STATS.map(({ value, label, icon }, i) => (
                            <div key={label} className="about-stat-card" data-aos="fade-up" data-aos-delay={i * 100}>
                                <div className="stat-icon">
                                    <FontAwesomeIcon icon={icon} />
                                </div>
                                <div className="stat-value">{value}</div>
                                <div className="stat-label">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Vision ── */}
            <section className="about-section about-section-alt">
                <div className="about-container about-two-col about-two-col-rev">
                    <div className="about-image-wrap" data-aos="fade-right">
                        <img width="1204" height="856" loading="lazy" decoding="async" src={work2} alt="Surya Group vision" className="about-img" />
                    </div>
                    <div className="about-text" data-aos="fade-left">
                        <span className="about-label">Where We're Headed</span>
                        <h2>Our Vision</h2>
                        <p>
                            Our vision is to lead the construction industry by delivering
                            top-quality estimation services through cutting-edge technology.
                            By integrating advanced estimating software, AI-driven tools, and
                            innovative plugins, we aim to make project planning and budgeting
                            more efficient, accurate, and accessible.
                        </p>
                        <p>
                            We envision a future where modern construction solutions are
                            seamless — empowering contractors and clients to achieve excellence
                            with precision and clarity in every project.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Values ── */}
            <section className="about-section">
                <div className="about-container">
                    <div className="about-section-header" data-aos="fade-up">
                        <span className="about-label">What Drives Us</span>
                        <h2>Our Core Values</h2>
                        <p>The principles that guide every estimate, every project, every client relationship.</p>
                    </div>
                    <div className="about-values-grid">
                        {VALUES.map(({ icon, title, desc }, i) => (
                            <div key={title} className="about-value-card" data-aos="fade-up" data-aos-delay={i * 120}>
                                <div className="value-icon">
                                    <FontAwesomeIcon icon={icon} />
                                </div>
                                <h3>{title}</h3>
                                <p>{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Team / Work Sample ── */}
            <section className="about-section about-section-alt">
                <div className="about-container about-two-col">
                    <div className="about-text" data-aos="fade-right">
                        <span className="about-label">Our Work</span>
                        <h2>Serving Contractors Across North America</h2>
                        <p>
                            From residential builds to large commercial projects, our team
                            delivers detailed, bankable estimates that help contractors win
                            bids and stay on budget. We serve general contractors, sub-contractors,
                            architects, engineers, and property owners.
                        </p>
                        <ul className="about-service-list">
                            <li>Flooring, Drywall &amp; Painting Estimation</li>
                            <li>Lumber, Concrete &amp; Masonry Estimation</li>
                            <li>MEP, Electrical &amp; Roofing Estimation</li>
                            <li>Demolition &amp; Insulation Estimation</li>
                        </ul>
                    </div>
                    <div className="about-image-wrap" data-aos="fade-left">
                        <img width="1324" height="879" loading="lazy" decoding="async" src={work3} alt="Surya Group services" className="about-img" />
                    </div>
                </div>
            </section>

            {/* ── CTA Banner ── */}
            <section className="about-cta-section" data-aos="fade-up">
                <div className="about-container">
                    <div className="about-cta-box">
                        <h2>Ready to Start Your Next Project?</h2>
                        <p>Get a precise estimate from our expert team — fast, reliable, and tailored to your needs.</p>
                        <div className="about-cta-btns">
                            <Link to="/contact-us" className="about-btn-primary">
                                Get a Free Quote <FontAwesomeIcon icon={faArrowRightLong} />
                            </Link>
                            <Link to="/services/flooring-estimation" className="about-btn-secondary">
                                Explore Services
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
}

export default About;
